import type { CSSProperties } from 'react'
import {
  ACCENT_GRADIENTS,
  COLORS,
  SURFACE_GRADIENTS,
  TEXT_GRADIENTS,
} from '@imobdesk/design-tokens'
import {
  ACCENT_BTN_GREEN,
  ACCENT_BTN_INDO_VO,
  SURFACE_CARD_LIGHT_INDO,
  SURFACE_HERO_DARK_INDO,
} from '@imobdesk/design-tokens/marketing'

function color(id: (typeof COLORS)[number]['id']) {
  const token = COLORS.find((c) => c.id === id)
  if (!token) throw new Error(`Missing design token color: ${id}`)
  return token.hex
}

function surfaceGradient(id: (typeof SURFACE_GRADIENTS)[number]['id']) {
  const token = SURFACE_GRADIENTS.find((s) => s.id === id)
  if (!token) throw new Error(`Missing design token surface gradient: ${id}`)
  return token
}

function textGradient(id: (typeof TEXT_GRADIENTS)[number]['id']) {
  const token = TEXT_GRADIENTS.find((t) => t.id === id)
  if (!token) throw new Error(`Missing design token text gradient: ${id}`)
  return token
}

function accentGradient(id: (typeof ACCENT_GRADIENTS)[number]['id']) {
  const token = ACCENT_GRADIENTS.find((g) => g.id === id)
  if (!token) throw new Error(`Missing design token accent gradient: ${id}`)
  return token
}

/** Paleta checkout — hex alinhados a `COLORS` + Tailwind padrão (slate/indigo/emerald). */
export const CHECKOUT_COLORS = {
  white: '#ffffff',
  slate950: color('slate-950'),
  slate900: color('slate-900'),
  slate600: color('slate-600'),
  slate100: color('slate-100'),
  slate50: color('slate-50'),
  indigo50: color('indigo-50'),
  indigo100: color('indigo-100'),
  indigo500: color('indigo-500'),
  indigo600: color('indigo-600'),
  violet500: color('violet-500'),
  emerald400: color('emerald-400'),
  emerald500: color('emerald-500'),
  green500: color('green-500'),
  /** Mid-tone do handoff `/contratar` (entre slate-950 e indigo-950). */
  checkoutMid: '#0b0a2e',
} as const

const radialHeroGlow = surfaceGradient('radial-hero-glow')
const textGradientGreen = textGradient('text-gradient-green')
const accentIndigoCta = accentGradient('indigo-cta')
const accentGreenCta = accentGradient('green-cta')

/** Gradientes derivados de `@imobdesk/design-tokens`. */
export const CHECKOUT_GRADIENTS = {
  /** `SURFACE_GRADIENTS.radial-hero-glow` — elipse ampliada no checkout. */
  darkHeroRadial: `radial-gradient(ellipse 90% 60% at 50% -10%, rgba(99, 102, 241, 0.22), transparent 70%)`,
  /** Base escura: slate-950 + mid-tone checkout + slate-950. */
  darkHeroLinear: `linear-gradient(180deg, ${CHECKOUT_COLORS.slate950} 0%, ${CHECKOUT_COLORS.checkoutMid} 50%, ${CHECKOUT_COLORS.slate950} 100%)`,
  /** Passo ativo → concluído (emerald-500 → indigo-500). */
  stepLineFill: `linear-gradient(90deg, ${CHECKOUT_COLORS.emerald500}, ${CHECKOUT_COLORS.indigo500})`,
  /** Modo escuro: emerald-400 → indigo-300. */
  stepLineFillDark: `linear-gradient(90deg, ${CHECKOUT_COLORS.emerald400}, #a5b4fc)`,
  /** `SURFACE_GRADIENTS.light-indigo-card` simplificado. */
  sumHead: `linear-gradient(135deg, ${CHECKOUT_COLORS.indigo50} 0%, ${CHECKOUT_COLORS.white} 100%)`,
  /** `TEXT_GRADIENTS.text-gradient-green` — badge trial corner. */
  trialCorner: textGradientGreen.css,
  /** `ACCENT_GRADIENTS.indigo-cta` — thumb bump / logo imob. */
  accentThumb: `linear-gradient(135deg, #312e81, ${CHECKOUT_COLORS.indigo600})`,
  /** `ACCENT_GRADIENTS.green-cta` — CTA principal. */
  ctaGreen: `linear-gradient(135deg, #4ade80, ${CHECKOUT_COLORS.green500})`,
} as const

/**
 * Nomes das custom properties usadas em `index.css` (bloco `.cx`).
 * Valores espelhados via `theme()` no CSS; gradientes complexos usam estas constantes.
 */
export const CHECKOUT_CSS_VARS = {
  '--cx-checkout-mid': CHECKOUT_COLORS.checkoutMid,
  '--cx-white': CHECKOUT_COLORS.white,
  '--cx-slate-950': CHECKOUT_COLORS.slate950,
  '--cx-slate-900': CHECKOUT_COLORS.slate900,
  '--cx-slate-600': CHECKOUT_COLORS.slate600,
  '--cx-slate-100': CHECKOUT_COLORS.slate100,
  '--cx-slate-50': CHECKOUT_COLORS.slate50,
  '--cx-indigo-50': CHECKOUT_COLORS.indigo50,
  '--cx-indigo-500': CHECKOUT_COLORS.indigo500,
  '--cx-indigo-600': CHECKOUT_COLORS.indigo600,
  '--cx-emerald-400': CHECKOUT_COLORS.emerald400,
  '--cx-emerald-500': CHECKOUT_COLORS.emerald500,
  '--cx-green-500': CHECKOUT_COLORS.green500,
  '--cx-dark-hero-radial': CHECKOUT_GRADIENTS.darkHeroRadial,
  '--cx-dark-hero-linear': CHECKOUT_GRADIENTS.darkHeroLinear,
  '--cx-step-line-fill': CHECKOUT_GRADIENTS.stepLineFill,
  '--cx-step-line-fill-dark': CHECKOUT_GRADIENTS.stepLineFillDark,
  '--cx-sum-head': CHECKOUT_GRADIENTS.sumHead,
  '--cx-accent-thumb': CHECKOUT_GRADIENTS.accentThumb,
  '--cx-trial-corner': CHECKOUT_GRADIENTS.ctaGreen,
  '--cx-radial-hero-glow': radialHeroGlow.css!,
} as const

/** Estilo inline para o container raiz `.cx` (Contratar / ContratacaoPublicShell). */
export const CHECKOUT_ROOT_STYLE = CHECKOUT_CSS_VARS as CSSProperties

/** Classes Tailwind de `@imobdesk/design-tokens/marketing` reutilizáveis no fluxo. */
export const CHECKOUT_MARKETING_CLASSES = {
  heroDarkIndigo: SURFACE_HERO_DARK_INDO,
  cardLightIndigo: SURFACE_CARD_LIGHT_INDO,
  accentBtnIndigo: ACCENT_BTN_INDO_VO,
  accentBtnGreen: ACCENT_BTN_GREEN,
  accentIndigoTailwind: accentIndigoCta.tailwind,
  accentGreenTailwind: accentGreenCta.tailwind,
} as const

/** Estilos inline usados em `Contratar.tsx` (substituem hex hardcoded). */
export const CHECKOUT_INLINE = {
  googleButton: {
    background: CHECKOUT_COLORS.white,
    color: '#1e293b' /* slate-800 */,
    border: '1px solid #e2e8f0' /* slate-200 */,
  },
  emailVerifiedPill: {
    background: '#ecfdf5' /* emerald-50 */,
    borderColor: '#d1fae5' /* emerald-100 */,
    color: '#047857' /* emerald-700 */,
  },
  emailPendingPill: {
    background: CHECKOUT_COLORS.indigo50,
    borderColor: '#c7d2fe' /* indigo-200 */,
    color: '#3730a3' /* indigo-800 */,
  },
} as const
